/**
 * AttentionStrip — the lead page's ONE "needs you" region (streamline D-3).
 *
 * WHAT IT REPLACES. The conversation card is a fixed-height column, and four
 * separate things had learned to dock inside it: the escalation banner at the
 * top of the thread, then the agent draft dock, the scheduled-messages bar, and
 * up to eight conditional composer warning lines. Live, three notice lines at
 * once was normal — and every line one of them took, the conversation lost. The
 * strip is a single region between the thread and the composer that holds all
 * of it: closed, one line that names what is waiting; open, the same controls
 * that were there before, one section at a time.
 *
 * WHAT IT DOES NOT REPLACE. The controls inside it gate what the AI agent sends
 * to real patients, so this file re-houses them rather than reimplementing
 * them. Drafts mount the real `AgentDraftDock` (`frameless`), so approve/reject
 * keeps its single implementation, its stale-inbound handling and its error
 * strip. Scheduled sends mount the real `ScheduledMessagesBar` (`frameless`),
 * so edit/cancel and the cancel confirm are untouched. Only the flag list is
 * new here, because mounting EscalationBanner inside a collapsible strip would
 * nest two collapse mechanics — its rows, filters, actions and button classes
 * are carried over as-is, and Claim/Resolve still POST through the parent's
 * callback (the same one the banner used) so ids and endpoints cannot drift.
 *
 * NEVER UNMOUNTED. Sections hide with the `hidden` class, they do not unmount —
 * a half-typed draft edit, a scheduled message mid-reschedule, or an opened
 * flag has to survive collapsing the strip and switching sections. The same
 * rule is why the whole strip hides rather than returning null when there is
 * nothing to show: ScheduledMessagesBar has to stay mounted for its fetch to
 * run, and its count is what tells this strip whether it has anything to say.
 */
import { useEffect, useState } from 'react';
import {
  AlertTriangle, Bot, ChevronDown, ChevronRight, ClipboardCheck, Clock, ExternalLink, Loader2, MessageSquareText,
} from 'lucide-react';
import type { AgentThreadState } from '../AgentPanel';
import {
  EscalationBreakdown,
  escalationAnchor,
  escalationConcern,
  escalationGuide,
  escalationLabel,
} from '../escalationDisplay';
import type { AgentEscalationLike, EscalationAnchor } from '../escalationDisplay';
import AgentDraftDock from '../agent/AgentDraftDock';
import ScheduledMessagesBar from '../ScheduledMessagesBar';
import { CountBadge } from '../ui';
import { ATTENTION_SEVERE_KINDS, buildAttentionSummary, visibleAttentionEscalations } from './attentionSummary';
import type { AttentionSection } from './attentionSummary';

export interface AttentionStripProps {
  contactId: string;
  /** Live agent-thread state lifted from AgentPanel via ContactDetail (null =
   *  no open agent conversation). Escalations, drafts, draftingSince and
   *  inFlight all ride here — the strip runs no fetch of its own. */
  agentThread: AgentThreadState | null;
  /** Bumped by the parent after a new schedule / fired send — passed straight
   *  through to ScheduledMessagesBar's reloadToken. */
  schedToken: number;
  /** Claim/resolve a flag. The SAME callback ContactDetail already passes to
   *  EscalationBanner (`escalationAction`) — the POST lives in the parent. */
  onEscalationAction: (id: string, action: 'claim' | 'resolve') => Promise<void>;
  /** Jump to where the concern was (thread message / screening detail). */
  onEscalationFocus: (anchor: EscalationAnchor) => void;
  onOpenConsole: () => void;
  /** After a draft approve/reject — the parent refreshes agent state + thread. */
  onDraftsChanged: () => void;
}

/** Where a flag's jump goes. Copied from EscalationBanner. */
const DESTINATION_LABEL: Record<'message' | 'screen', string> = {
  message: 'View message',
  screen: 'View screening',
};

// The flag panel's three controls, verbatim from EscalationBanner — a recruiter
// who learned Claim/Resolve on the banner must meet the same buttons here.
const FLAG_JUMP_BTN =
  'inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0';
const FLAG_CLAIM_BTN =
  'inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0';
const FLAG_RESOLVE_BTN =
  'inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 sm:min-h-0';

/** Section headers: always in reach, one body open at a time. */
const SECTION_HEADER =
  'flex min-h-[44px] w-full items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors duration-150 hover:bg-gray-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800/60 sm:min-h-0';

/**
 * Phone tier (D-12), CLOSED state only. On a phone the conversation card is the
 * whole screen, and a full-width tinted bar across the bottom of it reads as a
 * second wall rather than a one-line notice. Below `sm`, while collapsed, the
 * same button shrinks to a self-contained pill — tint, icon, truncating summary,
 * chevron, focus ring and the 44px target all unchanged.
 *
 * Every utility here is `max-sm:` AND it is only ever appended while collapsed:
 * from `sm` up, and in the expanded state at any width, the summary line is
 * byte-for-byte the bar it has always been (expanded, it has to read as the lid
 * of the panel underneath it).
 */
const COLLAPSED_PILL_MAX_SM =
  'max-sm:mx-2 max-sm:my-1.5 max-sm:w-auto max-sm:max-w-[calc(100%-1rem)] max-sm:self-start max-sm:rounded-full max-sm:border max-sm:border-gray-300 max-sm:dark:border-gray-600';

export default function AttentionStrip({
  contactId,
  agentThread,
  schedToken,
  onEscalationAction,
  onEscalationFocus,
  onOpenConsole,
  onDraftsChanged,
}: AttentionStripProps) {
  const [expanded, setExpanded] = useState(false);
  /** The one open section (accordion), or null when all are closed. */
  const [section, setSection] = useState<AttentionSection | null>(null);
  /** The one flag open to its guide + breakdown + actions. */
  const [openRow, setOpenRow] = useState<string | null>(null);
  /** The flag whose claim/resolve is in flight, and the last failure. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ id: string; msg: string } | null>(null);
  /** How many sends are queued — reported up by the bar, which is the only
   *  thing that knows (it owns the fetch) and renders nothing when it is 0. */
  const [schedCount, setSchedCount] = useState(0);

  const escalations = visibleAttentionEscalations<AgentEscalationLike>(agentThread?.escalations ?? []);
  const drafts = agentThread?.drafts ?? [];
  const draftingSince = agentThread?.draftingSince ?? null;
  const inFlight = agentThread?.inFlight ?? [];

  const summary = buildAttentionSummary({
    escalations,
    draftCount: drafts.length,
    drafting: Boolean(draftingSince),
    inFlightCount: inFlight.length,
    scheduledCount: schedCount,
  });

  const hasFlags = escalations.length > 0;
  /** The drafts section covers everything the agent is doing, in-flight included. */
  const hasAgentWork = drafts.length > 0 || Boolean(draftingSince) || inFlight.length > 0;
  const hasScheduled = schedCount > 0;
  /** Nothing waiting anywhere: the strip takes no space (but stays mounted). */
  const quiet = summary.segments.length === 0;

  // A different lead: drop the previous one's count immediately rather than
  // letting "2 scheduled" bleed across until the new fetch lands (the strip may
  // be re-used across leads without a remount).
  useEffect(() => { setSchedCount(0); }, [contactId]);

  // Went quiet: forget where the recruiter was, so whatever arrives next opens
  // on its own highest-priority section instead of a section that emptied.
  useEffect(() => {
    if (!quiet) return;
    setExpanded(false);
    setSection(null);
    setOpenRow(null);
    setActionError(null);
  }, [quiet]);

  // The open section emptied under them (last flag resolved, draft approved):
  // fall back to what matters most now rather than showing a blank body.
  const openSectionEmpty =
    (section === 'flags' && !hasFlags)
    || (section === 'drafts' && !hasAgentWork)
    || (section === 'scheduled' && !hasScheduled);
  useEffect(() => {
    if (section && openSectionEmpty) setSection(summary.openSection);
  }, [section, openSectionEmpty, summary.openSection]);

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    // First open lands on the thing that matters most; after that the strip
    // remembers where the recruiter was.
    if (next && !section) setSection(summary.openSection);
  };

  const toggleSection = (s: AttentionSection) => setSection((cur) => (cur === s ? null : s));

  /** Single-flight, exactly as EscalationBanner ran it: one busy row at a time,
   *  failures surfaced on the row instead of a toast the recruiter can miss. */
  const runAction = async (id: string, action: 'claim' | 'resolve') => {
    if (busyId) return;
    setBusyId(id);
    setActionError(null);
    try {
      await onEscalationAction(id, action);
    } catch {
      setActionError({ id, msg: `${action === 'claim' ? 'Claim' : 'Resolve'} failed — try again.` });
    } finally {
      setBusyId(null);
    }
  };

  const severe = summary.severity === 'severe';
  const tint = severe
    ? 'bg-red-50/70 dark:bg-red-900/15'
    : summary.severity === 'warn'
      ? 'bg-amber-50/70 dark:bg-amber-900/15'
      : 'bg-gray-50/70 dark:bg-gray-900/30';
  const SummaryIcon = hasFlags ? AlertTriangle : summary.severity === 'agent' ? Bot : Clock;
  const summaryIconCls = severe
    ? 'text-red-500'
    : summary.severity === 'warn'
      ? 'text-amber-500'
      : summary.severity === 'agent'
        ? 'text-primary-600 dark:text-primary-400'
        : 'text-gray-400';

  return (
    /* max-h-50% is measured against the CONVERSATION CARD (a flex column with a
       definite height): however much piles up, the thread keeps half the card
       and the strip scrolls inside itself. `hidden` (not null) when there is
       nothing to say — the scheduled bar underneath must stay mounted.
       The divider drops on phones while collapsed: the pill carries its own
       border there, and a full-width rule above a floating pill reads as two
       framing devices for one line. Expanded, the divider is back. */
    <div
      role="region"
      aria-label="Needs your attention on this lead"
      className={`flex max-h-[50%] flex-shrink-0 flex-col overflow-hidden border-t border-gray-200 dark:border-gray-700${expanded ? '' : ' max-sm:border-t-0'} ${quiet ? 'hidden' : ''}`}
    >
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls="lead-attention-body"
        className={`flex min-h-[44px] w-full flex-shrink-0 items-center gap-2 px-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 sm:min-h-0 sm:py-2 ${tint}${expanded ? '' : ` ${COLLAPSED_PILL_MAX_SM}`}`}
      >
        <SummaryIcon className={`h-4 w-4 flex-shrink-0 sm:h-3.5 sm:w-3.5 ${summaryIconCls}`} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200">
          {summary.segments.map((seg, i) => (
            <span
              key={seg}
              className={i === 0 ? 'font-semibold text-gray-900 dark:text-gray-100' : undefined}
            >
              {i > 0 ? ' · ' : ''}{seg}
            </span>
          ))}
        </span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* The body and every section inside it hide with `hidden` — nothing here
          is ever unmounted, so in-progress edits survive a collapse. */}
      <div
        id="lead-attention-body"
        className={`min-h-0 overflow-y-auto overscroll-contain ${expanded ? '' : 'hidden'}`}
      >
        {/* ---- Flags ---------------------------------------------------- */}
        <div className={hasFlags ? '' : 'hidden'}>
          <button
            type="button"
            onClick={() => toggleSection('flags')}
            aria-expanded={section === 'flags'}
            aria-controls="lead-attention-flags"
            className={SECTION_HEADER}
          >
            <AlertTriangle
              className={`h-3.5 w-3.5 flex-shrink-0 ${severe ? 'text-red-500' : 'text-amber-500'}`}
              aria-hidden="true"
            />
            Flags
            <CountBadge count={escalations.length} tone={severe ? 'alert' : 'primary'} />
            <ChevronDown
              className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${section === 'flags' ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>

          <ul id="lead-attention-flags" className={`px-2 pb-2 ${section === 'flags' ? '' : 'hidden'}`}>
            {escalations.map((e) => {
              const anchor = escalationAnchor(e);
              const concern = escalationConcern(e);
              const label = escalationLabel(e);
              const rowSevere = ATTENTION_SEVERE_KINDS.has(e.kind);
              const rowOpen = openRow === e.id;
              const guide = escalationGuide(e);
              const busy = busyId === e.id;
              const err = actionError?.id === e.id ? actionError.msg : null;

              return (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => { setOpenRow((k) => (k === e.id ? null : e.id)); setActionError(null); }}
                    aria-expanded={rowOpen}
                    aria-controls={`lead-attention-flag-${e.id}`}
                    className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-gray-100/70 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-800/60 sm:min-h-0"
                  >
                    <span className="flex min-w-0 flex-1 items-start gap-2">
                      <span
                        className={`mt-0.5 flex-shrink-0 ${rowSevere ? 'text-red-500' : 'text-amber-500'}`}
                        aria-hidden="true"
                      >
                        {e.kind === 'prescreen_flag'
                          ? <ClipboardCheck className="h-4 w-4" />
                          : <AlertTriangle className="h-4 w-4" />}
                      </span>
                      <span className="min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                        {e.status === 'claimed' && (
                          <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                            claimed
                          </span>
                        )}
                        {/* Phones give the concern its own line — inlined, it
                            wrapped mid-phrase and cost 2-3 lines a row. */}
                        {concern && (
                          <span className="mt-0.5 block text-xs text-gray-600 dark:text-gray-300 sm:mt-0 sm:ml-1 sm:inline">
                            <span className="hidden sm:inline">— </span>
                            {concern}
                          </span>
                        )}
                        {e.payload?.quote && (
                          <span className="mt-0.5 block truncate text-[11px] italic text-gray-500 dark:text-gray-400">
                            “{String(e.payload.quote)}”
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ml-auto mt-0.5 inline-flex flex-shrink-0 items-center gap-0.5 self-start text-[11px] font-medium text-primary-700 dark:text-primary-300 sm:mt-0 sm:self-center">
                      <span className="hidden sm:inline">{rowOpen ? 'Hide' : 'What to do'}</span>
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${rowOpen ? 'rotate-180' : ''}`} />
                    </span>
                  </button>

                  {/* Opened: the same context the console shows, plus the
                      actions — handling a flag never means leaving the lead. */}
                  {rowOpen && (
                    <div
                      id={`lead-attention-flag-${e.id}`}
                      className="mb-1 ml-2 rounded-lg border border-gray-200 bg-white/70 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40 sm:ml-8"
                    >
                      {guide && (
                        <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">{guide}</p>
                      )}
                      {e.payload?.guidance && (
                        <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                          {String(e.payload.guidance)}
                        </p>
                      )}
                      <EscalationBreakdown e={e} />

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {anchor && (
                          <button
                            type="button"
                            onClick={() => onEscalationFocus(anchor)}
                            className={FLAG_JUMP_BTN}
                          >
                            {anchor.kind === 'message'
                              ? <MessageSquareText className="h-3.5 w-3.5" />
                              : <ClipboardCheck className="h-3.5 w-3.5" />}
                            {DESTINATION_LABEL[anchor.kind]}
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {e.status === 'open' && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runAction(e.id, 'claim')}
                            title="I'm on it — assigns this to you, tells teammates it's taken, and stops any agent countdown"
                            className={FLAG_CLAIM_BTN}
                          >
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            Claim
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => runAction(e.id, 'resolve')}
                          title="It's handled — clears this flag (does not text the lead)"
                          className={FLAG_RESOLVE_BTN}
                        >
                          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          Resolve
                        </button>
                      </div>
                      {err && (
                        <p role="alert" className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">{err}</p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}

            <li>
              <button
                type="button"
                onClick={onOpenConsole}
                className="mt-0.5 flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-medium text-gray-500 transition-colors duration-150 hover:bg-gray-100/70 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800/60 dark:hover:text-gray-200 sm:min-h-0"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in the agent console
              </button>
            </li>
          </ul>
        </div>

        {/* ---- Drafts (and everything else the agent is doing) ----------- */}
        <div className={hasAgentWork ? '' : 'hidden'}>
          <button
            type="button"
            onClick={() => toggleSection('drafts')}
            aria-expanded={section === 'drafts'}
            aria-controls="lead-attention-drafts"
            className={SECTION_HEADER}
          >
            <Bot className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Drafts
            <CountBadge count={drafts.length} />
            {draftingSince && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 dark:text-primary-400">
                <span
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 dark:bg-primary-400"
                  aria-hidden="true"
                />
                writing…
              </span>
            )}
            <ChevronDown
              className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${section === 'drafts' ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>

          {/* The real dock: the writing pulse, the in-flight lines, the error
              strip and the DraftCards, with approve/reject unchanged. */}
          <div id="lead-attention-drafts" className={section === 'drafts' ? '' : 'hidden'}>
            <AgentDraftDock
              frameless
              drafts={drafts}
              draftingSince={draftingSince}
              inFlight={inFlight}
              onChanged={onDraftsChanged}
            />
          </div>
        </div>

        {/* ---- Scheduled ------------------------------------------------ */}
        <div className={hasScheduled ? '' : 'hidden'}>
          <button
            type="button"
            onClick={() => toggleSection('scheduled')}
            aria-expanded={section === 'scheduled'}
            aria-controls="lead-attention-scheduled"
            className={SECTION_HEADER}
          >
            <Clock className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Scheduled
            <CountBadge count={schedCount} />
            <ChevronDown
              className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${section === 'scheduled' ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>

          {/* Mounted at ALL times, including while this section is hidden and
              while the whole strip is quiet: its fetch is what produces the
              count that decides whether the strip has anything to say. */}
          <div id="lead-attention-scheduled" className={section === 'scheduled' ? '' : 'hidden'}>
            <ScheduledMessagesBar
              frameless
              contactId={contactId}
              reloadToken={schedToken}
              onCountChange={setSchedCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
